import React, { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { internalInviteSchema, schema } from "../../../../schemas/inviteUser";
import {
  RFHAutocomplete,
  RFHTextField,
  RHFCheckbox,
  RHFRadioGroup,
} from "../../../../components/RFH";
import {
  inviteExternalUser,
  inviteInternalUser,
} from "../../../../sevices/apiUser";
import { useInternalLdapUser } from "../../../../utils/reactQuery/users";
import { useResponsive } from "../../../../hooks/useResponsive";
import { useLDAPConnection } from "../../../../utils/reactQuery/LDAPConfigureTenant";
import CustomMenuItem from "../../../../components/Menu/CustomMenuItem";
import { CustomButton } from "../../../../components/Button/Button";
import { inviteUserTypes } from "../../../../utils/constants/index.js";
import useMutate from "../../../../hooks/useMutate.js";

function InviteUserForm({ onClose }) {
  const [apiError, setApiError] = React.useState("");
  // const [loading, setLoading] = React.useState(false);
  const [tab, setTab] = React.useState("externalUser");
  const { data } = useInternalLdapUser();
  const isMobile = useResponsive("down", "md");
  const { data: LDAPConnection } = useLDAPConnection();

  const isLDAPConnected = LDAPConnection?.data?.ldap_connection;
  const { handleSubmit, setValue, formState, watch, ...methods } =
    useForm({
      resolver: yupResolver(
        tab === "internalUser" ? internalInviteSchema : schema
      ),
      defaultValues: {
        tab: "externalUser", // Set default value for radio
      },
    });

  const tabValue = watch("tab");

  useEffect(() => {
    setTab(tabValue);
  }, [tabValue]);

  const { mutate, isPending: loading } = useMutate(
    tab === "internalUser" ? inviteInternalUser : inviteExternalUser,
    ["LDAPinternalList", "users"],
    () => {},
    () => {
      // setLoading(false);
      //toast.success(res.data.message);
      onClose();
    }
  );

  const submit = useCallback(
    (formData) => {
      setApiError("");

      if (tab === "internalUser") {
        const selected = data?.data?.data?.responseData?.filter(
          (ele, idx) => idx == formData.user
        )[0];
             
        mutate({
          first_name: selected.first_name,
          last_name: selected.last_name,
          email: selected.mail || "null",
        });
      } else {
        const newFormData = { ...formData };
        delete newFormData.tab;
        mutate(newFormData);
      }
    },
    [mutate, tab, data]
  );

  const userOptions = useMemo(() => {
    return (
      data?.data?.data?.responseData.map((ele, idx) => ({
        label: `${ele.first_name} ${ele.last_name}`,
        value: idx,
      })) || []
    );
  }, [data]);

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(submit)}
        noValidate
        className="invite-user-form"
      >
        <RHFRadioGroup
          name="tab"
          row={true}
          options={inviteUserTypes}
          isCircularRadio={true}
          className="invite-user-form-radio-group"
        />

        <Stack spacing={3} mt={2}>
          {apiError && <Alert severity="error">{apiError}</Alert>}

          {tab === "internalUser" ? (
            <Stack spacing={1}>
              <RFHAutocomplete
                name="user"
                label={"Select User"}
              options={userOptions}/>
              {/* <p style={{ color: "red" }}>{formState.errors?.user?.message}</p> */}
            </Stack>
          ) : (
            <Stack spacing={3}>
              <RFHTextField
                name="first_name"
                label={"First Name"}
                required
                fullWidth
                className="invite-user-form-text-field"
              />

              <RFHTextField
                name="last_name"
                label={"Last Name"}
                required
                fullWidth
                className="invite-user-form-text-field"
              />

              <RFHTextField
                name="email"
                label={"Email Address"}
                required
                fullWidth
                className="invite-user-form-text-field"
              />
            </Stack>
          )}
          <Box
            sx={{
              alignSelf: "end",
            }}
            className="invite-user-form-button-container d-flex"
          >
            <CustomButton
              type="submit"
              variant="contained"
              className="invite-user-form-submit-button"
              disabled={loading}
            >
              Add
            </CustomButton>
            <CustomButton
              variant="outlined"
              className="invite-user-form-cancel-button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </CustomButton>
          </Box>
        </Stack>
      </Box>
    </FormProvider>
  );
}

export default React.memo(InviteUserForm);
